<script setup>
/* global axios */
// Box "Participantes do grupo" — só p/ tickets de GRUPO (WhatsApp via AstraCall).
// Lista número + nome de quem está no grupo, com busca e paginação (5 por página),
// atualizando em tempo real:
//   - APRENDIDO: o backend aprende o autor de cada mensagem que chega.
//   - SYNC: com o endpoint de participantes do provedor, traz o roster COMPLETO.
// Serve de fonte para o seletor @@ no compositor (mencionar participante).
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});
const emit = defineEmits(['count']);
const route = useRoute();
const base = `/api/v1/accounts/${route.params.accountId}/kanban/connections`;

const PER_PAGE = 5;
const list = ref([]);
const loading = ref(true);
const syncing = ref(false);
const search = ref('');
const page = ref(1);
let pollId = null;

const fmtPhone = jid => {
  const digits = String(jid || '')
    .replace(/@.*/, '')
    .replace(/\D/g, '');
  return digits ? `+${digits}` : jid;
};
const displayName = p => p.name || fmtPhone(p.jid || p.number);

const total = computed(() => list.value.length);

const filtered = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return list.value;
  return list.value.filter(
    p =>
      (p.name || '').toLowerCase().includes(term) ||
      (p.number || '').includes(term)
  );
});
const totalPages = computed(() =>
  Math.max(1, Math.ceil(filtered.value.length / PER_PAGE))
);
const paged = computed(() =>
  filtered.value.slice((page.value - 1) * PER_PAGE, page.value * PER_PAGE)
);

// volta pra 1ª página ao buscar; mantém a página válida quando a lista muda
watch(search, () => {
  page.value = 1;
});
watch(totalPages, tp => {
  if (page.value > tp) page.value = tp;
});

const load = async (sync = false) => {
  if (sync) syncing.value = true;
  try {
    const { data } = await axios.get(`${base}/group_participants`, {
      params: { conversation_id: props.conversationId, sync: sync ? 1 : 0 },
    });
    list.value = Array.isArray(data.data) ? data.data : [];
    emit('count', list.value.length);
  } catch (e) {
    /* silencioso — o refresh reflete o estado real */
  } finally {
    loading.value = false;
    syncing.value = false;
  }
};

watch(
  () => props.conversationId,
  () => {
    page.value = 1;
    search.value = '';
    load(true);
  }
);
onMounted(() => {
  load(true); // 1ª carga tenta o roster completo do provedor
  pollId = setInterval(() => load(false), 15000); // tempo real do que foi aprendido
});
onBeforeUnmount(() => {
  if (pollId) clearInterval(pollId);
});
</script>

<template>
  <div class="flex flex-col gap-2.5 px-3.5 pt-2.5 pb-3.5">
    <!-- cabeçalho: contagem + atualizar -->
    <div class="flex items-center justify-between">
      <span class="text-xs font-medium text-n-slate-11">
        {{ total }} {{ total === 1 ? 'participante' : 'participantes' }}
      </span>
      <button
        type="button"
        class="inline-flex items-center gap-1 text-xs text-n-blue-11 bg-transparent hover:underline disabled:opacity-50"
        :disabled="syncing"
        @click="load(true)"
      >
        <span
          class="i-lucide-refresh-cw size-3"
          :class="{ 'animate-spin': syncing }"
        />
        Atualizar
      </button>
    </div>

    <!-- busca -->
    <div v-if="total > 0" class="relative">
      <span
        class="absolute inset-y-0 flex items-center pointer-events-none left-2.5"
      >
        <span class="i-lucide-search size-4 text-n-slate-10" />
      </span>
      <input
        v-model="search"
        type="text"
        placeholder="Buscar participante…"
        class="reset-base w-full h-8 pl-8 pr-2.5 text-sm rounded-lg bg-n-alpha-1 border border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
      />
    </div>

    <!-- estados -->
    <div v-if="loading" class="text-xs text-n-slate-10 py-1">Carregando…</div>
    <div
      v-else-if="!total"
      class="text-xs text-n-slate-10 py-1 leading-relaxed"
    >
      Ainda sem participantes. Eles aparecem conforme mandam mensagem no grupo
      (ou no próximo sync, se o provedor suportar a lista completa).
    </div>
    <div
      v-else-if="!filtered.length"
      class="text-xs text-n-slate-10 py-1"
    >
      Nenhum participante encontrado.
    </div>

    <!-- lista paginada (5 por página) -->
    <template v-else>
      <div
        v-for="p in paged"
        :key="p.jid || p.number"
        class="flex items-center gap-2.5 py-1.5 border-b border-n-weak last-of-type:border-b-0"
      >
        <Avatar
          :name="displayName(p)"
          :size="28"
          rounded-full
          class="shrink-0"
        />
        <div class="min-w-0 flex-1">
          <div class="text-sm text-n-slate-12 truncate">
            {{ displayName(p) }}
          </div>
          <div
            v-if="p.name && (p.number || p.jid)"
            class="text-[0.6875rem] text-n-slate-10 truncate tabular-nums"
          >
            {{ fmtPhone(p.jid || p.number) }}
          </div>
        </div>
        <span
          v-if="p.admin"
          class="shrink-0 text-[0.625rem] px-1.5 py-0.5 rounded-full bg-n-amber-3 text-n-amber-11 font-medium"
        >
          admin
        </span>
      </div>

      <!-- paginação -->
      <div
        v-if="totalPages > 1"
        class="flex items-center justify-between pt-1.5"
      >
        <button
          type="button"
          class="inline-flex items-center gap-1 text-xs text-n-slate-11 bg-transparent hover:text-n-slate-12 disabled:opacity-40 disabled:hover:text-n-slate-11"
          :disabled="page <= 1"
          @click="page -= 1"
        >
          <span class="i-lucide-chevron-left size-3.5" /> Anterior
        </button>
        <span class="text-[0.6875rem] text-n-slate-10 tabular-nums">
          {{ page }} / {{ totalPages }}
        </span>
        <button
          type="button"
          class="inline-flex items-center gap-1 text-xs text-n-slate-11 bg-transparent hover:text-n-slate-12 disabled:opacity-40 disabled:hover:text-n-slate-11"
          :disabled="page >= totalPages"
          @click="page += 1"
        >
          Próximo <span class="i-lucide-chevron-right size-3.5" />
        </button>
      </div>
    </template>
  </div>
</template>
